import { useForm, Controller } from 'react-hook-form';

import { BrandFormData } from '../../../../types/brand.types';

import { getTransformedCreateData, validateBrandData } from '../../brands-utils';
import { useSnackbar } from '../../../../hooks';

import './BrandsForm.css';
import { BrandsFormProps } from './BrandsForm.types';
/**
 * Renders form for creating new brands
 * Includes validation for ID, name, and order number with duplicate checking
 *
 * @param props - Component props
 * @returns JSX element containing the brand creation form
 */
export function BrandsForm({
  nextSuggestedId,
  nextSuggestedOrder,
  existingBrandIds,
  onSubmit
}: BrandsFormProps): JSX.Element {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid }
  } = useForm<BrandFormData>({
    defaultValues: {
      brandId: '',
      brandName: '',
      order: ''
    }
  });

  const { setSnackbarMessage } = useSnackbar();

  const handleFormSubmit = async (data: BrandFormData) => {
    const newBrand = getTransformedCreateData(data);
    const validationErrors = validateBrandData(newBrand);
    if (validationErrors.length > 0) {
      setSnackbarMessage(validationErrors.join(', '), 'error');
      return;
    }

    if (existingBrandIds.includes(newBrand.id)) {
      setSnackbarMessage('Бренд з таким ID вже існує. Оберіть інший ID.', 'error');
      return;
    }

    try {
      await onSubmit(data);
      reset();
    } catch (error) {
      // Error handled by parent
    }
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="brands-form">
      <div className="brands-form-row">
        <Controller
          name="brandId"
          control={control}
          rules={{ required: "ID обов'язковий" }}
          render={({ field }) => (
            <div className="brands-field-group id-field">
              <div className="brands-field-label">ID Бренд</div>
              <input
                {...field}
                type="number"
                min="1"
                step="1"
                placeholder={nextSuggestedId.toString()}
                className={`brands-form-input ${errors.brandId ? 'error' : ''}`}
                onKeyDown={e => {
                  if (e.key === '.' || e.key === '-' || e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }
                }}
              />
              {errors.brandId && <div className="brands-error-text">{errors.brandId.message}</div>}
            </div>
          )}
        />

        <Controller
          name="brandName"
          control={control}
          rules={{ required: "Назва обов'язкова" }}
          render={({ field }) => (
            <div className="brands-field-group name-field">
              <div className="brands-field-label">Назва Бренду</div>
              <input
                {...field}
                placeholder="Ввести назву"
                className={`brands-form-input ${errors.brandName ? 'error' : ''}`}
              />
              {errors.brandName && <div className="brands-error-text">{errors.brandName.message}</div>}
            </div>
          )}
        />

        <Controller
          name="order"
          control={control}
          rules={{ required: "Порядок обов'язковий" }}
          render={({ field }) => (
            <div className="brands-field-group order-field">
              <div className="brands-field-label">Порядок</div>
              <input
                {...field}
                type="number"
                // min="0"
                step="1"
                placeholder={nextSuggestedOrder.toString()}
                className={`brands-form-input ${errors.order ? 'error' : ''}`}
                onKeyDown={e => {
                  if (e.key === '.' || e.key === '-' || e.key === 'e' || e.key === 'E') {
                    e.preventDefault();
                  }
                }}
              />
              {errors.order && <div className="brands-error-text">{errors.order.message}</div>}
            </div>
          )}
        />

        <button type="submit" disabled={!isValid} className="brands-submit-btn">
          Створити
        </button>
      </div>
    </form>
  );
}
